import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import Table from 'rsuite/Table'
import Tooltip from 'rsuite/Tooltip'
import Whisper from 'rsuite/Whisper'
import Pagination from 'rsuite/Pagination'
import IconButton from 'rsuite/esm/IconButton'
import {FiEye} from 'react-icons/fi'
import Edit2 from '@rsuite/icons/Edit'
import {toast} from 'react-toastify'
import {isEmpty} from 'lodash'
import {StateParamsModel} from 'src/app/modules/common/Model'
import CheckPermissions from 'src/app/modules/common/checkPermission'
import DesignComponent from 'src/app/modules/common/components/designComponent'
import RSuiteTable from 'src/cms/helpers/components/rsuiteTable'
import * as icardRequestsReportRedux from '../redux'
import {service} from '../redux/service'
import ViewIcardRequestDetails from './ViewDetails'

const STATUS_OPTIONS = [
  {label: 'All', value: ''},
  {label: 'Pending', value: 'pending'},
  {label: 'Assigned', value: 'assigned'},
  {label: 'Resolved', value: 'resolved'},
  {label: 'Rejected', value: 'rejected'},
]

const IcardRequestsReport = () => {
  const dispatch = useDispatch()
  const {
    data: {data: requests, total},
    loading,
  } = useSelector((state: any) => state.icardRequestsReport)

  const [params, setParams] = useState<StateParamsModel>({page: 1, limit: 15})
  const [selectedMd5Id, setSelectedMd5Id] = useState<string | null>(null)
  const [showDetails, setShowDetails] = useState(false)
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState('')
  const [updatingStatus, setUpdatingStatus] = useState(false)

  useEffect(() => {
    dispatch(icardRequestsReportRedux.actions.getIcardRequestsReport(params))
  }, [params])

  const Cell = Table.Cell

  const StatusBadge = ({status}: {status: string}) => {
    const colorMap: Record<string, string> = {
      pending: 'info',
      assigned: 'warning',
      resolved: 'success',
      rejected: 'danger',
    }
    return (
      <span className={`badge badge-light-${colorMap[status] ?? 'secondary'}`}>{status}</span>
    )
  }

  const ActionCell = ({rowData, ...props}: any) => (
    <Cell {...props} className='link-group'>
      <CheckPermissions type='Edit'>
        <Whisper placement='top' trigger='hover' speaker={<Tooltip>View Details</Tooltip>}>
          <IconButton
            appearance='subtle'
            icon={<FiEye />}
            onClick={() => {
              setSelectedMd5Id(rowData.md5_id)
              setShowDetails(true)
            }}
          />
        </Whisper>
      </CheckPermissions>
      <CheckPermissions type='Edit'>
        <Whisper placement='top' trigger='hover' speaker={<Tooltip>Update Status</Tooltip>}>
          <IconButton
            appearance='subtle'
            icon={<Edit2 />}
            onClick={() => {
              setSelectedMd5Id(rowData.md5_id)
              setSelectedStatus(rowData.status)
              setShowStatusModal(true)
            }}
          />
        </Whisper>
      </CheckPermissions>
    </Cell>
  )

  const columns = [
    {label: 'S.N.', dataKey: 'sn', width: 60, cell: <Cell dataKey='sn' />},
    {
      label: 'Request Code',
      dataKey: 'icard_request_code',
      width: 150,
      cell: <Cell>{(rowData: any) => rowData.icard_request_code || '-'}</Cell>,
    },
    {
      label: 'Full Name',
      dataKey: 'full_name',
      flexGrow: 1,
      cell: <Cell>{(rowData: any) => rowData.full_name || '-'}</Cell>,
    },
    {
      label: 'Account Number',
      dataKey: 'account_number',
      flexGrow: 1,
      cell: <Cell>{(rowData: any) => rowData.account_number || '-'}</Cell>,
    },
    {
      label: 'Mobile',
      dataKey: 'mobile_number',
      width: 130,
      cell: <Cell>{(rowData: any) => rowData.mobile_number || '-'}</Cell>,
    },
    {
      label: 'Card Type',
      dataKey: 'card_type',
      width: 150,
      cell: <Cell>{(rowData: any) => rowData.card_type || '-'}</Cell>,
    },
    {
      label: 'Status',
      dataKey: 'status',
      width: 130,
      cell: <Cell>{(rowData: any) => <StatusBadge status={rowData.status} />}</Cell>,
    },
    {label: 'Action', width: 100, align: 'center', cell: <ActionCell dataKey='md5_id' />},
  ]

  const tableData = !isEmpty(requests)
    ? requests.map((item: any, i: number) => ({
        sn: (params.page - 1) * params.limit + (i + 1),
        md5_id: item.md5_id,
        icard_request_code: item.icard_request_code,
        full_name: item.full_name,
        account_number: item.account_number,
        mobile_number: item.mobile_number,
        card_type: item.card_type,
        status: item.status,
      }))
    : []

  const handleRefresh = () => {
    dispatch(icardRequestsReportRedux.actions.getIcardRequestsReport(params))
  }

  const handleUpdateStatus = () => {
    if (!selectedMd5Id || !selectedStatus) return
    setUpdatingStatus(true)
    service
      .updateIcardRequestStatus(selectedMd5Id, selectedStatus)
      .then(() => {
        toast.success('Status updated successfully.')
        setShowStatusModal(false)
        handleRefresh()
      })
      .catch(() => {
        toast.error('Failed to update status.')
      })
      .finally(() => setUpdatingStatus(false))
  }

  return (
    <>
      <div className='shadow p-3 bg-white rounded'>
        <DesignComponent
          moduleName='Icard Requests Report'
          params={params}
          setParams={setParams}
          filterData={true}
          handleRefresh={handleRefresh}
          addShow={false}
          deleteShow={false}
          importShow={false}
          toggleMultipleShow={false}
          enableDropdownFilter={true}
          dropdownOption={STATUS_OPTIONS}
          handleDropdownChange={(option: any) => {
            setParams({...params, status: option?.value, page: 1})
          }}
          dropdownValue={
            STATUS_OPTIONS.find((o) => o.value === String(params.status ?? '')) ||
            STATUS_OPTIONS[0]
          }
          dropdownPlaceholder='Filter by Status'
        />

        <div className='datatable'>
          <RSuiteTable
            onChecked={() => {}}
            showCheckbox={false}
            columns={columns}
            showLoading={loading}
            data={tableData}
            checkedValues={[]}
          />
          <Pagination
            prev
            next
            first
            last
            ellipsis
            boundaryLinks
            className='mt-5'
            maxButtons={5}
            size='sm'
            layout={['total', '-', 'limit', '|', 'pager', 'skip']}
            total={total || 0}
            limitOptions={[10, 20, 50]}
            limit={params.limit}
            activePage={params.page}
            onChangePage={(value) => setParams({...params, page: value})}
            onChangeLimit={(value) => setParams({...params, limit: value})}
          />
        </div>
      </div>

      {showDetails && selectedMd5Id && (
        <ViewIcardRequestDetails md5Id={selectedMd5Id} handleClose={() => setShowDetails(false)} />
      )}

      {showStatusModal && (
        <div className='modal show d-block' style={{background: 'rgba(0,0,0,0.5)'}}>
          <div className='modal-dialog modal-dialog-centered'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>Update Request Status</h5>
                <button className='btn-close' onClick={() => setShowStatusModal(false)} />
              </div>
              <div className='modal-body'>
                <label className='form-label fw-bold'>Status</label>
                <select
                  className='form-select'
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  <option value='pending'>Pending</option>
                  <option value='assigned'>Assigned</option>
                  <option value='resolved'>Resolved</option>
                  <option value='rejected'>Rejected</option>
                </select>
              </div>
              <div className='modal-footer'>
                <button className='btn btn-secondary btn-sm' onClick={() => setShowStatusModal(false)}>
                  Cancel
                </button>
                <button
                  className='btn btn-primary btn-sm'
                  onClick={handleUpdateStatus}
                  disabled={updatingStatus}
                >
                  {updatingStatus ? 'Updating...' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default IcardRequestsReport
